import { randomUUID } from "crypto";
import type { A2APluginConfig, Task } from "./types.js";

function stripTaskMarker(text: string, marker: string): string {
  const trimmed = text.trim();
  if (!trimmed.includes(marker)) return trimmed;
  return trimmed.slice(trimmed.indexOf(marker) + marker.length).trim();
}


function requestedFinalMarker(text: string): string | undefined {
  const patterns = [
    /termina(?:r)?\s+exactamente\s+con\s+([A-Z0-9_\-]+)/i,
    /end\s+(?:exactly\s+)?with\s+([A-Z0-9_\-]+)/i,
  ];
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match?.[1]) return match[1];
  }
  return undefined;
}

function withRequestedMarker(reply: string, text: string): string {
  const marker = requestedFinalMarker(text);
  if (!marker || reply.trim().endsWith(marker)) return reply;
  return `${reply.trim()} ${marker}`;
}

function runtimeOption(config: A2APluginConfig, key: string): string | undefined {
  const value = (config.runtime as Record<string, unknown> | undefined)?.[key];
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function agentIdFor(config: A2APluginConfig): string {
  return runtimeOption(config, "agentId") || config.agentName || "perkos-a2a-agent";
}

function llmSystemPrompt(config: A2APluginConfig): string {
  const agentName = config.agentName || config.runtime?.kind || "PerkOS A2A agent";
  return [
    `You are ${agentName}, a PerkOS A2A agent.`,
    "Reply concisely and directly.",
    "If asked to confirm receipt, confirm that you received the task via PerkOS A2A and are using PerkOS LLM.",
    "Do not mention implementation details unless asked.",
  ].join(" ");
}

/**
 * Generate a final task reply through PerkOS LLM when the local runtime accepts
 * a task but does not expose a synchronous final answer API.
 *
 * This keeps A2A task state machine semantics intact: delivery/wake still
 * happens, while the A2A caller receives a terminal task with a useful artifact
 * instead of being left in `working` indefinitely.
 */
export async function generateRuntimeReply(
  config: A2APluginConfig,
  text: string,
  marker: string
): Promise<string> {
  const runtimeKind = config.runtime?.kind || "openclaw";
  const apiKey = runtimeOption(config, "llmApiKey");
  if (!apiKey) {
    const agentName = config.agentName || config.runtime?.kind || "PerkOS A2A agent";
    const lower = text.toLowerCase();
    if (lower.includes("confirma") || lower.includes("confirm") || lower.includes("ping")) {
      return withRequestedMarker(`${agentName} recibió este mensaje vía PerkOS A2A.`, text);
    }
    return withRequestedMarker([
      `${agentName} recibió la tarea vía PerkOS A2A.`,
      "El runtime/LLM local no está configurado para generar una respuesta completa en este momento, pero el transporte A2A y el estado terminal funcionan.",
    ].join(" "), text);
  }

  const headers: Record<string, string> = {
    "content-type": "application/json",
    authorization: `Bearer ${apiKey}`,
    "x-agent-id": agentIdFor(config),
  };
  const system = llmSystemPrompt(config);
  const user = [
    text,
    "",
    "Return only the useful final answer.",
    `Do not include ${marker}; the A2A bridge will attach the marker/task result.`,
  ].join("\n");

  if (runtimeKind === "hermes") {
    const base = (runtimeOption(config, "openAiBaseUrl") || "https://api.llm.perkos.xyz/v1").replace(/\/+$/, "");
    const model = runtimeOption(config, "model") || "kimi-k2.6:cloud";
    const response = await fetch(`${base}/chat/completions`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        model,
        messages: [
          { role: "system", content: system },
          { role: "user", content: user },
        ],
        temperature: 0.2,
      }),
    });

    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new Error(`PerkOS LLM OpenAI-compatible call failed (${response.status}): ${body || response.statusText}`);
    }

    const data = await response.json() as any;
    return stripTaskMarker(data.choices?.[0]?.message?.content || "Task completed.", marker);
  }

  const base = (runtimeOption(config, "ollamaBaseUrl") || "https://api.llm.perkos.xyz").replace(/\/+$/, "");
  const model = runtimeOption(config, "model") || "kimi-k2.6:cloud";
  const response = await fetch(`${base}/api/chat`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      model,
      stream: false,
      messages: [
        { role: "system", content: system },
        { role: "user", content: user },
      ],
    }),
  });

  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new Error(`PerkOS LLM Ollama-compatible call failed (${response.status}): ${body || response.statusText}`);
  }

  const data = await response.json() as any;
  return stripTaskMarker(data.message?.content || data.response || "Task completed.", marker);
}

export function completeTaskWithReply(task: Task, finalText: string): void {
  task.artifacts.push({
    kind: "artifact",
    artifactId: randomUUID(),
    parts: [{ kind: "text", text: finalText }],
  });
  task.status = {
    state: "completed",
    timestamp: new Date().toISOString(),
    message: {
      role: "agent",
      parts: [{ kind: "text", text: finalText }],
    },
  };
}

export function isTerminalTaskState(state: unknown): boolean {
  return state === "completed" || state === "failed" || state === "canceled";
}
